import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import LockIcon from '@lib/assets/icons/extended_lock.svg?react';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Typography from '@lib/components/Typography/Typography';
import { FEATURE_LOCK_BANNER } from '@lib/enums/localStorageKeys';
import FeatureLockHelpPopup, {
  FeatureLockHelpPopupContent,
} from './FeatureLockHelpPopup';
import styles from './FeatureLockBanner.module.scss';

type Props = {
  bannerId: string;
  description: React.ReactNode;
  helpPopup?: FeatureLockHelpPopupContent;
  onUpgrade: () => void;
  title: React.ReactNode;
};

type DismissedMap = Record<string, boolean>;

export default function FeatureLockBanner({
  bannerId,
  description,
  helpPopup,
  onUpgrade,
  title,
}: Props) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useLocalStorage<DismissedMap>(
    FEATURE_LOCK_BANNER,
    {},
  );
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  if (dismissed[bannerId]) return null;

  const onClose = () => setDismissed((prev) => ({ ...prev, [bannerId]: true }));

  return (
    <>
      <div className={styles.root}>
        <span className={styles.icon}>
          <LockIcon />
        </span>
        <div className={styles.content}>
          <Typography variant="body" strong className={styles.title}>
            {title}
          </Typography>
          <Typography variant="caption" className={styles.desc}>
            {description}
          </Typography>
        </div>
        <div className={styles.actions}>
          {helpPopup && (
            <IconButton
              icon="help"
              symbolsOutlined
              size={IconButtonSizes.Medium}
              type={IconButtonTypes.PrimaryOutlined}
              onClick={() => setIsHelpOpen(true)}
            />
          )}
          <Button
            buttonText={t('upgrade-plan')}
            buttonType={ButtonTypes.primaryOutlined}
            onClick={onUpgrade}
          />
          <IconButton
            icon="close"
            size={IconButtonSizes.Medium}
            type={IconButtonTypes.PrimaryOutlined}
            onClick={onClose}
          />
        </div>
      </div>
      {helpPopup && (
        <FeatureLockHelpPopup
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          onUpgrade={onUpgrade}
          title={helpPopup.title}
          subtitle={helpPopup.subtitle}
          sections={helpPopup.sections}
          upgradeButtonText={helpPopup.upgradeButtonText}
        />
      )}
    </>
  );
}
